/*
  Model-number search for the parts diagrams (Schematics menu + /pages/parts-diagrams).
  The list of every live page is assets/schematic-models.json, generated by
  scripts/schematic_index_build.py. It is fetched only when someone uses the box.
  Rows: [brand index, title, handle, aliases?]. The last result always offers a product search,
  so a part number typed here still finds the part.
*/
(() => {
  if (customElements.get('schematic-finder')) return;
  const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '');
  const esc = (s) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  let loading = null;

  function load(src) {
    if (!loading) {
      loading = fetch(src).then((r) => r.json()).then((d) => {
        const brands = d.b;
        return {
          brands: brands.map((b) => ({ name: b, key: norm(b) })),
          rows: d.m.map(([b, title, handle, aliases]) => {
            const brand = brands[b];
            const model = title.toLowerCase().startsWith(brand.toLowerCase()) ? title.slice(brand.length) : title;
            return { brand, title, url: `/pages/schematics/${handle}`, model: norm(model),
                     key: norm(`${brand} ${title} ${aliases || ''}`) };
          }),
        };
      }).catch((e) => { loading = null; throw e; });
    }
    return loading;
  }

  // 0 exact model, 1 model starts with it, 2 anywhere in title/aliases; every word must match
  function search(data, q, limit) {
    const words = q.split(/\s+/).map(norm).filter(Boolean);
    if (!words.length || words.join('').length < 2) return { brand: null, hits: [], total: 0 };
    const whole = words.join('');
    const brand = data.brands.find((b) => b.key === whole) || null;
    const hits = [];
    for (const r of data.rows) {
      if (!words.every((w) => r.key.includes(w))) continue;
      const rank = r.model === whole ? 0 : r.model.startsWith(whole) ? 1 : 2;
      hits.push([rank, r]);
    }
    hits.sort((a, b) => a[0] - b[0] || a[1].title.localeCompare(b[1].title, undefined, { numeric: true }));
    return { brand, hits: hits.slice(0, limit).map((h) => h[1]), exact: hits.length && hits[0][0] === 0, total: hits.length };
  }

  class SchematicFinder extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.list = this.querySelector('[role="listbox"]');
      this.status = this.querySelector('[data-status]');
      this.limit = parseInt(this.dataset.limit || '8', 10);
      this.active = -1;
      this.input.addEventListener('focus', () => load(this.dataset.src).catch(() => {}), { once: true });
      this.input.addEventListener('input', () => this.run());
      this.input.addEventListener('keydown', (e) => this.key(e));
      this.querySelector('form').addEventListener('submit', (e) => { e.preventDefault(); this.go(); });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.close(); });
      // The desktop dropdown closes when the mouse leaves it; keep it open while someone is typing here.
      const item = this.closest('.mega-menu__item');
      if (item) {
        item.addEventListener('mouseleave', (e) => {
          if (document.activeElement === this.input && this.input.value) e.stopImmediatePropagation();
        }, true);
      }
      const q = new URLSearchParams(location.search).get('q');
      if (q && this.dataset.readQuery !== undefined) { this.input.value = q; this.run(); }
    }

    async run() {
      const q = this.input.value.trim();
      if (q.length < 2) return this.close();
      let data;
      try { data = await load(this.dataset.src); } catch (e) { return this.render(q, { brand: null, hits: [], total: 0 }); }
      if (this.input.value.trim() !== q) return;
      this.result = search(data, q, this.limit);
      this.render(q, this.result);
    }

    render(q, res) {
      const items = [];
      if (res.brand) {
        items.push(`<li role="option"><a href="${esc(this.brandUrl(res.brand.name))}"><b>All ${esc(res.brand.name)} diagrams</b></a></li>`);
      }
      for (const r of res.hits) {
        items.push(`<li role="option"><a href="${esc(r.url)}"><b>${esc(r.title)}</b><small> parts diagram</small></a></li>`);
      }
      if (res.total > res.hits.length) {
        items.push(`<li class="schematic-finder__more">${res.total.toLocaleString()} matches. Type more of the model number to narrow it down.</li>`);
      }
      items.push(`<li role="option" class="schematic-finder__parts"><a href="/search?q=${encodeURIComponent(q)}">Search parts for “${esc(q)}” &rarr;</a></li>`);
      this.list.innerHTML = items.join('');
      this.list.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.active = -1;
      this.list.querySelectorAll('[role="option"]').forEach((li, i) => { li.id = `${this.id}-opt-${i}`; });
      if (this.status) this.status.textContent = res.total ? `${res.total} diagrams found` : 'No diagram found';
    }

    brandUrl(name) {
      const a = document.querySelector(`.schematic-chips a[data-brand="${CSS.escape(name)}"], .schematic-brand-grid a[data-brand="${CSS.escape(name)}"]`);
      return a ? a.getAttribute('href') : '/pages/parts-diagrams#all-brands';
    }

    options() { return Array.from(this.list.querySelectorAll('[role="option"]')); }

    key(e) {
      // Stop the mega menu's arrow-key handling from moving to the next menu while typing here.
      if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) { e.stopPropagation(); return; }
      if (e.key === 'Escape' && !this.list.hidden) { e.stopPropagation(); this.close(); return; }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const opts = this.options();
      if (!opts.length || this.list.hidden) return;
      e.preventDefault();
      e.stopPropagation();
      this.active = (this.active + (e.key === 'ArrowDown' ? 1 : -1) + opts.length) % opts.length;
      opts.forEach((o, i) => o.classList.toggle('is-active', i === this.active));
      this.input.setAttribute('aria-activedescendant', opts[this.active].id);
      opts[this.active].scrollIntoView({ block: 'nearest' });
    }

    async go() {
      const q = this.input.value.trim();
      if (!q) return;
      const opts = this.options();
      if (this.active >= 0 && opts[this.active]) { location.href = opts[this.active].querySelector('a').href; return; }
      await this.run();
      const res = this.result;
      if (!res || !res.total) { location.href = `/search?q=${encodeURIComponent(q)}`; return; }
      if (res.exact || res.total === 1) { location.href = res.hits[0].url; return; }
      if (res.brand && res.total > this.limit) { location.href = this.brandUrl(res.brand.name); return; }
      if (this.dataset.readQuery === undefined) location.href = `/pages/parts-diagrams?q=${encodeURIComponent(q)}`;
    }

    close() {
      this.list.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.active = -1;
    }
  }
  customElements.define('schematic-finder', SchematicFinder);
})();
